import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  useGetUserUsedPaidCouponsMutation, 
  useGetUserUsedDownloadedCouponsMutation 
} from 'redux/api/commerce.api';
import { transformApiData } from '../../../utils/functions';
import { ITEMS_PER_PAGE } from '../../../utils/enums';

export const useCouponsData = () => {
  const [getUserUsedPaidCoupons] = useGetUserUsedPaidCouponsMutation();
  const [getUserUsedDownloadedCoupons] = useGetUserUsedDownloadedCouponsMutation();

  const [paidBenefitsData, setPaidBenefitsData] = useState([]);
  const [downloadedBenefitsDataApi, setDownloadedBenefitsDataApi] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingDownloaded, setIsLoadingDownloaded] = useState(true);
  const [filterCounts, setFilterCounts] = useState({
    totalActiveBenefitsCount: 0,
    totalCancelledBenefitsCount: 0,
    totalArchivedBenefitsCount: 0,
  });

  const paginationRefs = {
    paid: { loading: useRef(false), hasMore: useRef(true), page: useRef(0) },
    downloaded: { loading: useRef(false), hasMore: useRef(true), page: useRef(0) }
  };

  const fetchPaidCoupons = useCallback(async (page, isInitial = false, filterBy) => {
    const refs = paginationRefs.paid;
    if (refs.loading.current && !isInitial) return null;
    if (isInitial) { setIsLoading(true); refs.hasMore.current = true; refs.page.current = 0; }
    else { refs.loading.current = true; }

    try {
      const response = await getUserUsedPaidCoupons({ limit: ITEMS_PER_PAGE, page, filterBy }).unwrap();
      const { benefits, hasNext, totalActiveBenefitsCount, totalCancelledBenefitsCount, totalArchivedBenefitsCount } = transformApiData(response);
      const counts = {
        totalActiveBenefitsCount: totalActiveBenefitsCount || 0,
        totalCancelledBenefitsCount: totalCancelledBenefitsCount || 0,
        totalArchivedBenefitsCount: totalArchivedBenefitsCount || 0,
      };
      refs.hasMore.current = hasNext;
      if (isInitial) {
        setPaidBenefitsData(benefits);
        setFilterCounts(counts);
      } else {
        setPaidBenefitsData(prev => mergeBenefits(prev, benefits));
        refs.page.current = page;
      }
      return counts;
    } catch { refs.hasMore.current = false; return null; }
    finally { setIsLoading(false); refs.loading.current = false; }
  }, [getUserUsedPaidCoupons]);

  const fetchDownloadedCoupons = useCallback(async (page, isInitial = false) => {
    const refs = paginationRefs.downloaded;
    if (refs.loading.current && !isInitial) return;
    if (isInitial) { setIsLoadingDownloaded(true); refs.hasMore.current = true; refs.page.current = 0; }
    else { refs.loading.current = true; }

    try {
      const response = await getUserUsedDownloadedCoupons({ limit: ITEMS_PER_PAGE, page }).unwrap();
      const { benefits, hasNext } = transformApiData(response);
      refs.hasMore.current = hasNext;
      if (isInitial) setDownloadedBenefitsDataApi(benefits);
      else {
        setDownloadedBenefitsDataApi(prev => mergeBenefits(prev, benefits));
        refs.page.current = page;
      }
    } catch { refs.hasMore.current = false; }
    finally { setIsLoadingDownloaded(false); refs.loading.current = false; }
  }, [getUserUsedDownloadedCoupons]);

  const mergeBenefits = (prev, newBenefits) => {
    const merged = [...prev];
    newBenefits.forEach(newB => {
      const idx = merged.findIndex(b => b.id === newB.id);
      if (idx >= 0) {
        merged[idx] = { ...merged[idx], ...newB };
      } else merged.push(newB);
    });
    return merged;
  };

  useEffect(() => {
    fetchPaidCoupons(0, true, undefined);
    fetchDownloadedCoupons(0, true);
  }, [fetchPaidCoupons, fetchDownloadedCoupons]);

  const refreshData = useCallback(async (isPaidTab, paidFilters, downloadedFilters) => {
    if (isPaidTab) {
      const { selectedFilter } = paidFilters || {};
      const counts = await fetchPaidCoupons(0, true, selectedFilter);
      // Return the updated filter counts
      return counts;
    } else {
      await fetchDownloadedCoupons(0, true);
      return null;
    }
  }, [fetchPaidCoupons, fetchDownloadedCoupons]);

  return {
    paidBenefitsData,
    downloadedBenefitsDataApi,
    isLoading: isLoading || isLoadingDownloaded,
    paginationRefs,
    fetchPaidCoupons,
    fetchDownloadedCoupons,
    refreshData,
    filterCounts
  };
};